/**
 * Department Jobs-To-Be-Done registry — the canonical, data-only definition of
 * what every department EXISTS TO DO, expressed as a small set of recurring
 * Jobs. This is the load-bearing primitive of the operating surface: dashboards,
 * Play, audits, and onboarding all DERIVE from these jobs (see
 * docs/departments/DEPARTMENT-JTBD-IMPLEMENTATION-PRD.md).
 *
 * One file. Data only — no React, no icons, no imports (the body is
 * lockstep-mirrored verbatim to the MCP package, like section-registry.ts).
 *
 * Two kinds of consumer:
 *   • Definition (this file) — canonical, reviewable, diffable. The source of
 *     truth for purpose, owner DEFAULTS, maturity DEFAULTS, and the four
 *     capability states (the tooling backlog).
 *   • State (excellent_department_jobs, migration v61) — per-workspace overrides
 *     (assigned owner, achieved maturity, snooze). Joined to definitions by the
 *     TEXT `id` slug. Never the reverse: definitions live in code.
 *
 * Departments = the 9 workspace sections (section-registry.ts) ∪ the synthetic
 * **Agents** department. Agents is workspace-scoped (the /agents panel), NOT a
 * per-project section — the agent workforce/gate/goals/loops are workspace
 * global (PRD §4).
 *
 * Lockstep mirror: packages/excellent-mcp/src/jtbd-registry.ts. Body must stay
 * byte-identical (the doc header may differ). Enforced by
 * `pnpm check-jtbd-lockstep`; behavior locked by `pnpm test-jtbd`.
 *
 * Review scores: the Operations + Agents jobs carry persona-panel consensus
 * (JTBD-PERSONAS.md). Other departments' `review` blocks are seeded estimates
 * pending a full panel pass (the run-time re-scoring loop is PRD §13.2, a
 * fast-follow). They seed Play ranking and the gap-matrix ordering today.
 */
// ---------------- registry ----------------
/**
 * Single source of truth for the hiring capability's display name (decision
 * D5: one label, "Hiring", everywhere — slugs/tables/URLs stay "hiring").
 * Defined here (not imported) to respect this file's no-imports invariant;
 * every other display of the hiring department name — the capability
 * manifest, the section registry, MCP tool defaults, breadcrumb/settings
 * labels — imports this constant so the names can't drift apart again.
 */
export const HIRING_DISPLAY_NAME = "Hiring";
const OPERATIONS = {
    slug: "operations",
    name: "Operations",
    kind: "section",
    scope: "project",
    mission: "Keep the business substrate legible and resilient — entity, vendors, tooling, and the people accountable — so nothing critical is undocumented or single-threaded.",
    managerQuestion: "Is the company's foundation documented and resilient?",
    northStar: {
        id: "operations.resilience-coverage",
        label: "Resilience coverage",
        unit: "percent",
        direction: "increase",
        target: { kind: "fixed", value: 90 },
        source: "section_audit_findings (resolved vs open) + operations brief completeness",
        jobRefs: [
            "operations.document-substrate",
            "operations.eliminate-spofs",
            "operations.keep-vendors-current",
        ],
        description: "Share of critical functions, vendors, and tools documented, owned, and backed up.",
    },
    secondaryKpis: [
        {
            id: "operations.spofs-unmitigated",
            label: "SPOFs without mitigation",
            unit: "count",
            direction: "decrease",
            target: { kind: "fixed", value: 0 },
            source: "section_audit_findings tagged single-point-of-failure",
            jobRefs: ["operations.eliminate-spofs"],
            description: "Critical functions with no documented backup.",
        },
        {
            id: "operations.vendor-gaps",
            label: "Vendor records incomplete",
            unit: "count",
            direction: "decrease",
            target: { kind: "fixed", value: 0 },
            source: "excellent_section_briefs (tooling fields missing owner/renewal)",
            jobRefs: ["operations.keep-vendors-current"],
            description: "Core tools missing an owner or renewal date.",
        },
        {
            id: "operations.audit-age",
            label: "Audit freshness",
            unit: "days",
            direction: "decrease",
            target: { kind: "fixed", value: 30 },
            source: "section_audits.last_run_at (operations)",
            jobRefs: ["operations.document-substrate"],
            description: "Days since the operations audit last ran.",
        },
        {
            id: "operations.brief-completeness",
            label: "Brief completeness",
            unit: "percent",
            direction: "increase",
            target: { kind: "fixed", value: 100 },
            source: "excellent_section_briefs (operations) completeness",
            jobRefs: ["operations.document-substrate"],
            description: "How fully the operations brief is filled.",
        },
    ],
    jobs: [
        {
            id: "operations.document-substrate",
            statement: "When the entity / vendor / tooling record is missing or stale, I want it captured so the company is legible, auditable, and resilient.",
            cadence: "monthly",
            appliesWhen: "always",
            trigger: {
                source: "section_audit_findings",
                condition: "open operations findings, or required operations brief fields incomplete",
            },
            definitionOfDone: "Entity, locations, ops lead, tooling/vendors documented and fresh within the 30-day audit cadence.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: { state: "exists", ref: "operations brief + audit profile" },
            workflow: { state: "exists", ref: "operations section audit (spawner)" },
            dashboard: { state: "exists", ref: "open-findings attention rollup" },
            playAction: {
                state: "partial",
                note: "findings surface via attention but not as discrete Play cards",
            },
            review: { priority: 3.7, confidence: 4.3, impact: 3.0, spread: 1.4 },
        },
        {
            id: "operations.eliminate-spofs",
            statement: "When a critical function, tool, or person has no backup, I want it flagged and a mitigation queued so the business survives a disruption.",
            cadence: "monthly",
            appliesWhen: "team",
            trigger: {
                source: "section_audit_findings",
                condition: "audit finding tagged as a single point of failure",
            },
            definitionOfDone: "Every identified SPOF has a documented owner and mitigation.",
            owner: "hybrid",
            maturity: "manual",
            tooling: {
                state: "partial",
                note: "audit names SPOFs but there is no SPOF register",
            },
            workflow: {
                state: "gap",
                note: "audit detects; no remediation workflow exists",
            },
            dashboard: { state: "gap", note: "folded into generic findings" },
            playAction: { state: "gap" },
            review: { priority: 2.7, confidence: 2.2, impact: 3.8, spread: 1.8 },
        },
        {
            id: "operations.keep-vendors-current",
            statement: "When a vendor/tool record lacks an owner, renewal date, or has gone unreviewed, I want it refreshed so spend and access stay under control.",
            cadence: "monthly",
            appliesWhen: "team",
            trigger: {
                source: "excellent_section_briefs",
                condition: "tooling/vendor brief fields stale or missing an owner",
            },
            definitionOfDone: "Every core tool has a recorded owner and a known renewal/review date.",
            owner: "hybrid",
            maturity: "manual",
            tooling: { state: "partial", ref: "operations brief (tooling fields)" },
            workflow: { state: "gap", note: "no vendor-review workflow" },
            dashboard: { state: "gap" },
            playAction: { state: "gap" },
            review: { priority: 2.6, confidence: 3.0, impact: 2.8, spread: 1.0 },
        },
    ],
    boundaryNotes: "Operations shed its agent-engine jobs (gate/workers/goals/loops) to the Agents department per PRD §4. What remains is the classic business substrate.",
};
const AGENTS = {
    slug: "agents",
    name: "Agents",
    kind: "synthetic",
    scope: "workspace",
    mission: "Keep the autonomous agent workforce productive and pointed at real outcomes — the ship/verify gate flowing, workers healthy, goals current, and automation cheap — so the founder's attention goes only where a human is required.",
    managerQuestion: "Is the agent engine running and efficient?",
    northStar: {
        id: "agents.verified-throughput",
        label: "Verified throughput",
        unit: "count",
        direction: "increase",
        target: { kind: "trend" },
        source: "tasks shipped through the gate per week (cost overlay: worker_runs)",
        jobRefs: ["agents.keep-gate-flowing", "agents.optimize-loops"],
        description: "Verified-done work the engine produces — output over cost.",
    },
    secondaryKpis: [
        {
            id: "agents.gate-latency",
            label: "Gate latency",
            unit: "days",
            direction: "decrease",
            target: { kind: "fixed", value: 1 },
            source: "tasks: median review → done",
            jobRefs: ["agents.keep-gate-flowing"],
            description: "How fast the ship/verify gate clears.",
        },
        {
            id: "agents.worker-success",
            label: "Worker success",
            unit: "percent",
            direction: "increase",
            target: { kind: "fixed", value: 90 },
            source: "excellent_worker_runs succeeded / total (24h)",
            jobRefs: ["agents.keep-workers-healthy"],
            description: "Are workers succeeding or failing?",
        },
        {
            id: "agents.goals-on-cadence",
            label: "Goals on cadence",
            unit: "percent",
            direction: "increase",
            target: { kind: "fixed", value: 100 },
            source: "excellent_goals active within cadence / active",
            jobRefs: ["agents.point-work-at-goals"],
            description: "Is autonomous work pointed at live goals?",
        },
        {
            id: "agents.spend-vs-budget",
            label: "Automation spend",
            unit: "percent",
            direction: "hold-below",
            target: { kind: "brief", field: "agents.budgetPct", fallback: 100 },
            source: "telemetry/loop variant cost vs budget (range)",
            jobRefs: ["agents.optimize-loops"],
            description: "Is the engine within budget?",
        },
    ],
    jobs: [
        {
            id: "agents.keep-gate-flowing",
            statement: "When work piles up in review, I want it triaged (approved / rejected / reassigned) so throughput doesn't stall behind the ship/verify gate.",
            cadence: "continuous",
            appliesWhen: "has-agents",
            trigger: {
                source: "tasks",
                condition: "tasks.status='review' aging past the freshness window",
            },
            definitionOfDone: "No review item older than the freshness window; queue depth under threshold.",
            owner: "hybrid",
            maturity: "agent-assisted",
            tooling: { state: "exists", ref: "/tasks?view=review" },
            workflow: {
                state: "exists",
                ref: "verifier worker + autopilot review→verifier spawn",
            },
            dashboard: { state: "exists", ref: "Awaiting review KPI + attention" },
            playAction: {
                state: "partial",
                note: "review backlog can surface but isn't a first-class Play card",
            },
            review: { priority: 4.3, confidence: 4.2, impact: 4.0, spread: 0.9 },
        },
        {
            id: "agents.keep-workers-healthy",
            statement: "When worker runs fail or exceed budget, I want the cause surfaced and the worker fixed or paused so the agent workforce stays productive.",
            cadence: "continuous",
            appliesWhen: "has-agents",
            trigger: {
                source: "excellent_worker_runs",
                condition: "runs failed or budget-exceeded in last 24h, or orphaned running rows stale by started_at",
            },
            definitionOfDone: "Zero unaddressed failed runs; no orphaned running rows.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: { state: "exists", ref: "/agents/runs, worker_run_list" },
            workflow: {
                state: "partial",
                note: "autopilot watchdog exists; orphan-run reaping is a known gap",
            },
            dashboard: { state: "exists", ref: "Agent runs (24h) + success % KPI" },
            playAction: { state: "gap" },
            review: { priority: 4.0, confidence: 3.3, impact: 4.2, spread: 1.3 },
        },
        {
            id: "agents.point-work-at-goals",
            statement: "When active goals drift past cadence or lack a plan, I want them re-pointed, planned, or closed so agent effort targets real outcomes.",
            cadence: "weekly",
            appliesWhen: "has-agents",
            trigger: {
                source: "excellent_goals",
                condition: "active goal past its cadence window or in needs-plan health",
            },
            definitionOfDone: "Every active goal is in-progress or paused-with-reason; none stale.",
            owner: "hybrid",
            maturity: "agent-assisted",
            tooling: { state: "exists", ref: "/agents/goals, goal_list" },
            workflow: {
                state: "exists",
                ref: "goal reconciliation (token-free pass each autopilot tick)",
            },
            dashboard: { state: "exists", ref: "Active goals KPI + past-cadence attention" },
            playAction: { state: "gap" },
            review: { priority: 3.5, confidence: 3.7, impact: 3.8, spread: 1.5 },
        },
        {
            id: "agents.optimize-loops",
            statement: "When a workflow falls below its pass-rate floor or spend spikes, I want the cheapest passing variant promoted (or a rollback) so the engine stays efficient.",
            cadence: "weekly",
            appliesWhen: "has-agents",
            trigger: {
                source: "excellent_loops",
                condition: "loop below pass-rate floor, or automation spend WoW spike",
            },
            definitionOfDone: "No loop below floor; automation spend within budget.",
            owner: "hybrid",
            maturity: "agent-assisted",
            tooling: { state: "exists", ref: "/agents/workflows, loop_list" },
            workflow: {
                state: "exists",
                ref: "Crucible optimizer + self-healing watchdog (last_good rollback)",
            },
            dashboard: {
                state: "partial",
                note: "spend KPI + loops-promoted insight; pass-floor breaches not a headline",
            },
            playAction: {
                state: "gap",
                note: "optimizer verdicts route to the review inbox, not Play",
            },
            review: { priority: 2.8, confidence: 3.0, impact: 3.7, spread: 2.0 },
        },
    ],
};
const DESIGN = {
    slug: "design",
    name: "Design",
    kind: "section",
    scope: "project",
    mission: "Keep the product visually coherent — one design system, consistently applied — so the surface reads as crafted, not assembled.",
    managerQuestion: "Is the design system healthy and consistently applied?",
    northStar: {
        id: "design.system-health",
        label: "Design-system health",
        unit: "score",
        direction: "increase",
        target: { kind: "fixed", value: 90 },
        source: "excellent_section_audits.score (design) over runs",
        jobRefs: ["design.enforce-design-system", "design.clear-design-debt"],
        description: "Composite consistency score from the design audit + consistency guards.",
    },
    secondaryKpis: [
        {
            id: "design.open-findings",
            label: "Open findings",
            unit: "count",
            direction: "decrease",
            target: { kind: "fixed", value: 0 },
            source: "section_audit_findings (design) open by severity",
            jobRefs: ["design.enforce-design-system"],
            description: "Unresolved design-consistency findings.",
        },
        {
            id: "design.drift-rate",
            label: "Drift rate",
            unit: "count",
            direction: "decrease",
            target: { kind: "trend" },
            source: "section_audit_findings (design) new per cadence",
            jobRefs: ["design.clear-design-debt"],
            description: "New findings per audit cadence — is debt compounding?",
        },
        {
            id: "design.audit-age",
            label: "Days since audit",
            unit: "days",
            direction: "decrease",
            target: { kind: "fixed", value: 7 },
            source: "section_audits.last_run_at (design)",
            jobRefs: ["design.enforce-design-system"],
            description: "Staleness of the design audit score.",
        },
        {
            id: "design.brief-completeness",
            label: "Brief completeness",
            unit: "percent",
            direction: "increase",
            target: { kind: "fixed", value: 100 },
            source: "excellent_section_briefs (design) completeness",
            jobRefs: ["design.document-source-of-truth"],
            description: "Tokens, ownership, and cadence documented.",
        },
    ],
    jobs: [
        {
            id: "design.enforce-design-system",
            statement: "When a surface drifts from the design system (hand-rolled primitives, off-scale spacing, raw color), I want it flagged so the product stays coherent.",
            cadence: "weekly",
            appliesWhen: "always",
            trigger: {
                source: "section_audit_findings",
                condition: "open design findings from the consistency guards",
            },
            definitionOfDone: "No open design-consistency findings above low severity.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: {
                state: "exists",
                ref: "design audit + check-*-consistency guards",
            },
            workflow: { state: "exists", ref: "design section audit" },
            dashboard: { state: "exists", ref: "audit score + open findings KPIs" },
            playAction: { state: "partial" },
            review: { priority: 3.0, confidence: 4.0, impact: 3.2, spread: 1.2 },
        },
        {
            id: "design.clear-design-debt",
            statement: "When design findings accumulate, I want them fixed (ideally agent-applied) so visual debt doesn't compound across releases.",
            cadence: "weekly",
            appliesWhen: "always",
            trigger: {
                source: "section_audit_findings",
                condition: "design findings open longer than one audit cadence",
            },
            definitionOfDone: "Open design findings trend down release over release.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: { state: "exists", ref: "Fix-with-agent on findings" },
            workflow: { state: "partial", note: "fix agent exists; not auto-dispatched" },
            dashboard: { state: "exists", ref: "days-since-audit + in-review KPIs" },
            playAction: { state: "gap" },
            review: { priority: 2.8, confidence: 3.4, impact: 3.0, spread: 1.1 },
        },
        {
            id: "design.document-source-of-truth",
            statement: "When the design brief (tokens, ownership, audit cadence) is incomplete, I want it filled so the system has a documented source of truth.",
            cadence: "monthly",
            appliesWhen: "always",
            trigger: {
                source: "excellent_section_briefs",
                condition: "required design brief fields incomplete",
            },
            definitionOfDone: "Design brief completeness ≥ required threshold.",
            owner: "human",
            maturity: "manual",
            tooling: { state: "exists", ref: "design brief" },
            workflow: { state: "gap" },
            dashboard: { state: "exists", ref: "brief completeness KPI" },
            playAction: { state: "partial" },
            review: { priority: 2.4, confidence: 3.8, impact: 2.6, spread: 1.0 },
        },
    ],
};
const HIRING = {
    slug: "hiring",
    name: HIRING_DISPLAY_NAME,
    kind: "section",
    scope: "project",
    mission: "Fill open roles fast and well, and keep the candidate pipeline moving with consistent, fair evaluation.",
    managerQuestion: "Are we filling roles, fast and well?",
    northStar: {
        id: "hiring.time-to-fill",
        label: "Time-to-fill",
        unit: "days",
        direction: "decrease",
        target: { kind: "brief", field: "hiring.timeToFillSlaDays", fallback: 30 },
        source: "hiring_applications: median req-open → hired per period",
        jobRefs: ["hiring.fill-open-roles", "hiring.keep-pipeline-moving"],
        description: "Median days from opening a role to a hire.",
    },
    secondaryKpis: [
        {
            id: "hiring.open-reqs",
            label: "Open requisitions",
            unit: "count",
            direction: "hold-below",
            target: { kind: "trend" },
            source: "hiring_jobs.status='open'",
            jobRefs: ["hiring.fill-open-roles"],
            description: "Seats currently to fill.",
        },
        {
            id: "hiring.offer-acceptance",
            label: "Offer acceptance",
            unit: "percent",
            direction: "increase",
            target: { kind: "fixed", value: 80 },
            source: "hiring_applications: hires / offers",
            jobRefs: ["hiring.fill-open-roles"],
            description: "The quality half of fast-and-well.",
        },
        {
            id: "hiring.stage-conversion",
            label: "Screen → onsite",
            unit: "percent",
            direction: "increase",
            target: { kind: "trend" },
            source: "hiring_applications: stage conversion",
            jobRefs: ["hiring.keep-pipeline-moving"],
            description: "Pipeline quality through the funnel.",
        },
        {
            id: "hiring.stalled-candidates",
            label: "Candidates stalled",
            unit: "count",
            direction: "decrease",
            target: { kind: "fixed", value: 0 },
            source: "hiring_applications: in-stage past SLA",
            jobRefs: ["hiring.keep-pipeline-moving"],
            description: "Good candidates lost to latency.",
        },
    ],
    jobs: [
        {
            id: "hiring.fill-open-roles",
            statement: "When a role is open and under-pipelined, I want sourcing and outreach moving so time-to-fill stays low.",
            cadence: "weekly",
            appliesWhen: "team",
            trigger: {
                source: "applications",
                condition: "open job with thin or aging candidate pipeline",
            },
            definitionOfDone: "Every open role has an active pipeline meeting its stage targets.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: { state: "exists", ref: "Hiring product (jobs, candidates)" },
            workflow: { state: "partial", ref: "open-req-fanout loop" },
            dashboard: { state: "exists", ref: "Hiring product dashboard" },
            playAction: { state: "gap" },
            review: { priority: 3.4, confidence: 3.2, impact: 3.6, spread: 1.6 },
        },
        {
            id: "hiring.keep-pipeline-moving",
            statement: "When candidates stall in a stage, I want them advanced or closed so good candidates aren't lost to latency.",
            cadence: "daily",
            appliesWhen: "team",
            trigger: {
                source: "applications",
                condition: "candidate in a stage past its SLA with no activity",
            },
            definitionOfDone: "No candidate stalled beyond stage SLA.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: { state: "exists", ref: "Hiring pipeline board" },
            workflow: { state: "gap" },
            dashboard: { state: "exists", ref: "pipeline-by-stage" },
            playAction: { state: "gap" },
            review: { priority: 3.2, confidence: 3.3, impact: 3.4, spread: 1.2 },
        },
        {
            id: "hiring.ensure-scorecard-coverage",
            statement: "When interviews happen without submitted scorecards, I want them collected so decisions are evidence-based and fair.",
            cadence: "weekly",
            appliesWhen: "team",
            trigger: {
                source: "applications",
                condition: "completed interview without a submitted scorecard",
            },
            definitionOfDone: "Every completed interview has a submitted scorecard before the decision.",
            owner: "human",
            maturity: "tool-assisted",
            tooling: { state: "exists", ref: "scorecard_submit" },
            workflow: { state: "gap" },
            dashboard: { state: "partial" },
            playAction: { state: "gap" },
            review: { priority: 2.8, confidence: 3.5, impact: 3.0, spread: 1.0 },
        },
    ],
};
const DEVELOPMENT = {
    slug: "development",
    name: "Engineering",
    kind: "section",
    scope: "project",
    mission: "Ship reviewed, traceable code at a healthy cadence with green CI and managed technical debt.",
    managerQuestion: "Is engineering shipping, traceably and reviewed?",
    northStar: {
        id: "development.review-latency",
        label: "Review latency",
        unit: "days",
        direction: "decrease",
        target: {
            kind: "brief",
            field: "development.reviewLatencyTargetDays",
            fallback: 1,
        },
        source: "tasks: median review → done per day",
        jobRefs: ["development.ship-reviewed-code"],
        description: "Median time work waits in review before it ships.",
    },
    secondaryKpis: [
        {
            id: "development.ship-velocity",
            label: "Ship velocity",
            unit: "count",
            direction: "increase",
            target: { kind: "trend" },
            source: "excellent_commits per range (or verified tasks)",
            jobRefs: ["development.ship-reviewed-code"],
            description: "Delivery throughput — velocity that leads outcomes.",
        },
        {
            id: "development.traceability",
            label: "Traceability",
            unit: "percent",
            direction: "increase",
            target: { kind: "fixed", value: 90 },
            source: "tasks done with ≥1 linked commit",
            jobRefs: ["development.ship-reviewed-code"],
            description: "Done work tied to a commit.",
        },
        {
            id: "development.ci-health",
            label: "CI / hard-gate health",
            unit: "count",
            direction: "decrease",
            target: { kind: "fixed", value: 0 },
            source: "open tasks titled [ci-bounce]% (post-mirror hard-gate script_failure) + ci-signal lastHardGate",
            jobRefs: ["development.keep-ci-green"],
            description: "Open hard-gate CI bounce work (code failures on linux/macos), not advisory windows quota.",
        },
        {
            id: "development.stale-branches",
            label: "Stale branches",
            unit: "count",
            direction: "decrease",
            target: { kind: "fixed", value: 0 },
            source: "excellent_branches idle 30+ days",
            jobRefs: ["development.manage-tech-debt"],
            description: "Branches in flight with no recent movement.",
        },
    ],
    jobs: [
        {
            id: "development.ship-reviewed-code",
            statement: "When changes are ready, I want them reviewed and shipped through the gate so velocity doesn't come at the cost of quality.",
            cadence: "continuous",
            appliesWhen: "always",
            trigger: {
                source: "tasks",
                condition: "engineering tasks in review aging, or branches open without a PR",
            },
            definitionOfDone: "PR review latency under target; nothing merged unreviewed.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: { state: "exists", ref: "ship/verify gate, /issues?view=review" },
            workflow: { state: "exists", ref: "verifier worker" },
            dashboard: { state: "exists", ref: "commits + in-review KPIs" },
            playAction: { state: "partial" },
            review: { priority: 3.8, confidence: 3.8, impact: 3.6, spread: 1.0 },
        },
        {
            id: "development.keep-ci-green",
            statement: "When tests, lint, or build break, I want it surfaced and fixed so the tree stays releasable.",
            cadence: "continuous",
            appliesWhen: "always",
            trigger: {
                source: "gitlab hard-gate pipeline (post-mirror ci-signal)",
                condition: "linux/macos script_failure after mirror push; local ci-parity-build verification gate on package diffs",
            },
            definitionOfDone: "No open [ci-bounce] tasks; last hard-gate snapshot not failed; package diffs owe ci-parity-build.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: {
                state: "exists",
                ref: "ci-signal.ts + gate:pnpm ci-parity-build + development.ci-health oracle",
            },
            workflow: { state: "exists", ref: "post-mirror poll → bounce task → shipper" },
            dashboard: { state: "exists", ref: "development.ci-health (open CI-bounce count)" },
            playAction: {
                state: "exists",
                ref: "claim [ci-bounce] task / shipper re-ship",
            },
            review: { priority: 3.6, confidence: 3.4, impact: 3.8, spread: 1.1 },
        },
        {
            id: "development.manage-tech-debt",
            statement: "When tech-debt findings accumulate, I want them tracked and prioritized so debt is paid down deliberately, not by surprise.",
            cadence: "monthly",
            appliesWhen: "always",
            trigger: {
                source: "section_audit_findings",
                condition: "engineering findings open beyond one cadence",
            },
            definitionOfDone: "Tech-debt findings have owners and a trend.",
            owner: "human",
            maturity: "manual",
            tooling: { state: "partial" },
            workflow: { state: "gap" },
            dashboard: { state: "partial" },
            playAction: { state: "gap" },
            review: { priority: 2.6, confidence: 3.0, impact: 3.0, spread: 1.0 },
        },
        {
            id: "development.document-architecture",
            statement: "When stack/repo/environment docs are missing or stale, I want them captured so engineering knowledge isn't single-threaded.",
            cadence: "monthly",
            appliesWhen: "always",
            trigger: {
                source: "excellent_section_briefs",
                condition: "required engineering brief fields incomplete",
            },
            definitionOfDone: "Engineering brief completeness ≥ required threshold.",
            owner: "human",
            maturity: "manual",
            tooling: { state: "exists", ref: "engineering brief + Docs tab" },
            workflow: { state: "gap" },
            dashboard: { state: "exists", ref: "docs-freshness KPI" },
            playAction: { state: "partial" },
            review: { priority: 2.4, confidence: 3.6, impact: 2.8, spread: 1.0 },
        },
    ],
};
const MARKETING = {
    slug: "marketing",
    name: "Marketing",
    kind: "section",
    scope: "project",
    mission: "Keep the funnel filling from known channels, in a consistent brand voice, with analytics you can act on.",
    managerQuestion: "Is the funnel filling, and where from?",
    northStar: {
        id: "marketing.qualified-leads",
        label: "Qualified new leads",
        unit: "count",
        direction: "increase",
        target: { kind: "trend" },
        source: "crm_leads created per day vs trailing baseline",
        jobRefs: [
            "marketing.keep-funnel-filling",
            "marketing.maintain-channel-coverage",
        ],
        description: "New leads entering the funnel against trend.",
    },
    secondaryKpis: [
        {
            id: "marketing.sessions",
            label: "Sessions",
            unit: "count",
            direction: "increase",
            target: { kind: "trend" },
            source: "analytics provider (sessions in range)",
            jobRefs: ["marketing.keep-funnel-filling"],
            description: "Traffic at the top of the funnel.",
        },
        {
            id: "marketing.lead-opp-rate",
            label: "Lead → opp rate",
            unit: "percent",
            direction: "increase",
            target: { kind: "trend" },
            source: "crm: opps / leads in range",
            jobRefs: ["marketing.keep-funnel-filling"],
            description: "Lead quality — do leads convert?",
        },
        {
            id: "marketing.channel-coverage",
            label: "Channel coverage",
            unit: "percent",
            direction: "increase",
            target: { kind: "fixed", value: 100 },
            source: "committed channels active this cadence / total",
            jobRefs: ["marketing.maintain-channel-coverage"],
            description: "Committed channels still publishing.",
        },
        {
            id: "marketing.icp-coverage",
            label: "ICP coverage",
            unit: "percent",
            direction: "increase",
            target: { kind: "fixed", value: 100 },
            source: "market_icp_profiles completeness",
            jobRefs: ["marketing.keep-funnel-filling"],
            description: "Do we know who we sell to?",
        },
    ],
    jobs: [
        {
            id: "marketing.keep-funnel-filling",
            statement: "When new-lead volume dips below trend, I want the cause surfaced and a channel pushed so the top of the funnel stays full.",
            cadence: "weekly",
            appliesWhen: "always",
            trigger: {
                source: "crm_leads",
                condition: "new-lead volume below trailing trend",
            },
            definitionOfDone: "Lead volume holds at or above trend across the range.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: { state: "partial", ref: "marketing dashboard (traffic/leads)" },
            workflow: { state: "partial", ref: "content + lifecycle loops" },
            dashboard: { state: "exists", ref: "sessions + new-leads KPIs" },
            playAction: { state: "gap" },
            review: { priority: 3.4, confidence: 3.0, impact: 3.6, spread: 1.4 },
        },
        {
            id: "marketing.maintain-channel-coverage",
            statement: "When a committed channel goes quiet (no publishes), I want it flagged so coverage doesn't silently lapse.",
            cadence: "weekly",
            appliesWhen: "always",
            trigger: {
                source: "section_audit_findings",
                condition: "marketing audit finds a channel without recent activity",
            },
            definitionOfDone: "Every committed channel has activity within its cadence.",
            owner: "hybrid",
            maturity: "manual",
            tooling: { state: "partial" },
            workflow: { state: "partial", ref: "single-person-content-engine loop" },
            dashboard: { state: "partial" },
            playAction: { state: "gap" },
            review: { priority: 2.8, confidence: 2.8, impact: 3.0, spread: 1.2 },
        },
        {
            id: "marketing.enforce-brand-voice",
            statement: "When published content drifts from brand voice, I want it caught so the brand reads consistently.",
            cadence: "monthly",
            appliesWhen: "always",
            trigger: {
                source: "section_audit_findings",
                condition: "marketing audit flags brand-voice inconsistency",
            },
            definitionOfDone: "No open brand-voice findings.",
            owner: "hybrid",
            maturity: "manual",
            tooling: { state: "partial", ref: "brand voice brief field" },
            workflow: { state: "gap" },
            dashboard: { state: "gap" },
            playAction: { state: "gap" },
            review: { priority: 2.2, confidence: 2.6, impact: 2.6, spread: 1.0 },
        },
        {
            id: "marketing.maintain-seo-health",
            statement: "When SEO basics slip (missing sitemap/robots, thin titles/meta, no target keywords, stale content cadence), I want it surfaced and fixed so organic discovery keeps compounding.",
            cadence: "monthly",
            appliesWhen: "always",
            trigger: {
                source: "section_audit_findings",
                condition: "marketing audit flags SEO basics (titles/meta/OG, sitemap, robots) or SEO setup checks are open",
            },
            definitionOfDone: "SEO setup checks satisfied and no open SEO audit findings.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: { state: "exists", ref: "marketing SEO workbench (/marketing/seo)" },
            workflow: { state: "exists", ref: "document-seo-optimizer loop" },
            dashboard: { state: "partial", ref: "SEO setup + on-page findings on the SEO tab" },
            playAction: { state: "partial", ref: "SEO checklist checks → Play" },
            review: { priority: 2.9, confidence: 3.0, impact: 3.2, spread: 1.2 },
        },
    ],
};
const SALES = {
    slug: "sales",
    name: "Sales",
    kind: "section",
    scope: "project",
    mission: "Move open pipeline toward the number — qualify what comes in, advance what's live, and close what's committed — on a clean CRM.",
    managerQuestion: "Will we hit the number, and which deals need me?",
    northStar: {
        id: "sales.forecast-attainment",
        label: "Forecast attainment",
        unit: "percent",
        direction: "increase",
        target: { kind: "fixed", value: 100 },
        source: "crm_opportunities: cumulative weighted forecast vs quota pace (brief quota)",
        jobRefs: ["sales.advance-open-pipeline", "sales.close-committed-deals"],
        description: "Weighted forecast as a share of the quota for the period.",
    },
    secondaryKpis: [
        {
            id: "sales.open-pipeline",
            label: "Open pipeline value",
            unit: "currency",
            direction: "hold-above",
            target: { kind: "trend" },
            source: "crm_opportunities open Σ value_minor",
            jobRefs: ["sales.advance-open-pipeline"],
            description: "Is there enough in flight to hit the number?",
        },
        {
            id: "sales.win-rate",
            label: "Win rate 90d",
            unit: "percent",
            direction: "increase",
            target: { kind: "trend" },
            source: "crm_opportunities closed_won / closed (90d)",
            jobRefs: ["sales.advance-open-pipeline"],
            description: "Is conversion holding?",
        },
        {
            id: "sales.closing-this-week",
            label: "Closing this week",
            unit: "count",
            direction: "increase",
            target: { kind: "trend" },
            source: "crm_opportunities expected_close_date in week",
            jobRefs: ["sales.close-committed-deals"],
            description: "What needs a push now.",
        },
        {
            id: "sales.sales-cycle",
            label: "Sales cycle",
            unit: "days",
            direction: "decrease",
            target: { kind: "trend" },
            source: "crm: lead created → won median",
            jobRefs: ["sales.advance-open-pipeline"],
            description: "Deal velocity, lead to won.",
        },
    ],
    jobs: [
        {
            id: "sales.qualify-inbound-leads",
            statement: "When new leads arrive, I want them qualified and routed quickly so good fit isn't lost to slow follow-up.",
            cadence: "daily",
            appliesWhen: "has-customers",
            trigger: {
                source: "crm_leads",
                condition: "new or unattended leads with no activity in 24h",
            },
            definitionOfDone: "Every new lead is qualified or disqualified within SLA.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: { state: "exists", ref: "CRM (leads)" },
            workflow: { state: "partial", ref: "inbound-lead-to-meeting loop" },
            dashboard: { state: "exists", ref: "CRM section dashboard" },
            playAction: { state: "gap" },
            review: { priority: 3.6, confidence: 3.4, impact: 3.8, spread: 1.4 },
        },
        {
            id: "sales.advance-open-pipeline",
            statement: "When deals stall, I want them advanced or closed so pipeline reflects reality and forecast is trustworthy.",
            cadence: "daily",
            appliesWhen: "has-customers",
            trigger: {
                source: "crm_opportunities",
                condition: "open opp with no activity 14+ days, or sequence step due",
            },
            definitionOfDone: "No open deal idle beyond the staleness window.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: { state: "exists", ref: "CRM (opportunities, sequences)" },
            workflow: { state: "partial" },
            dashboard: { state: "exists", ref: "pipeline-by-stage" },
            playAction: { state: "gap" },
            review: { priority: 3.8, confidence: 3.6, impact: 4.0, spread: 1.2 },
        },
        {
            id: "sales.close-committed-deals",
            statement: "When deals are past expected close, I want them pushed or re-dated so the forecast doesn't rot.",
            cadence: "weekly",
            appliesWhen: "has-customers",
            trigger: {
                source: "crm_opportunities",
                condition: "expected_close_date < today and still open",
            },
            definitionOfDone: "No overdue-to-close deal left un-updated.",
            owner: "human",
            maturity: "tool-assisted",
            tooling: { state: "exists", ref: "CRM (opportunities)" },
            workflow: { state: "gap" },
            dashboard: { state: "exists", ref: "closing-this-week KPI" },
            playAction: { state: "gap" },
            review: { priority: 3.6, confidence: 3.6, impact: 3.8, spread: 1.0 },
        },
        {
            id: "sales.keep-crm-clean",
            statement: "When leads/opps are missing contacts or stage data, I want them completed so reporting and automation can trust the data.",
            cadence: "weekly",
            appliesWhen: "has-customers",
            trigger: {
                source: "crm_leads",
                condition: "leads/opps missing a linked person/org or stage",
            },
            definitionOfDone: "No open record missing its required links.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: { state: "exists", ref: "CRM" },
            workflow: { state: "gap" },
            dashboard: { state: "partial" },
            playAction: { state: "gap" },
            review: { priority: 2.8, confidence: 3.6, impact: 2.8, spread: 0.9 },
        },
    ],
};
const CUSTOMER_SUCCESS = {
    slug: "customer-success",
    name: "Support",
    kind: "section",
    scope: "project",
    mission: "Keep customers successful and retained — triage what comes in, catch risk before it churns, and drive renewals — with self-healing help content.",
    managerQuestion: "Which accounts are at risk, and what's renewing?",
    northStar: {
        id: "customer-success.retention-health",
        label: "Retention health",
        unit: "percent",
        direction: "increase",
        target: { kind: "fixed", value: 90 },
        source: "crm_leads (won) ⋈ activities/transactions recency — healthy share",
        jobRefs: [
            "customer-success.surface-at-risk-accounts",
            "customer-success.drive-renewals",
        ],
        description: "Share of the customer book in the healthy band.",
    },
    secondaryKpis: [
        {
            id: "customer-success.at-risk",
            label: "At-risk accounts",
            unit: "count",
            direction: "decrease",
            target: { kind: "fixed", value: 0 },
            source: "customers with no touch 60+ days or negative signal",
            jobRefs: ["customer-success.surface-at-risk-accounts"],
            description: "Accounts that may churn without intervention.",
        },
        {
            id: "customer-success.renewals-due",
            label: "Renewals due",
            unit: "count",
            direction: "hold-below",
            target: { kind: "trend" },
            source: "renewal cadence within the window",
            jobRefs: ["customer-success.drive-renewals"],
            description: "Renewals approaching their date.",
        },
        {
            id: "customer-success.engagement-recency",
            label: "Engagement recency",
            unit: "days",
            direction: "decrease",
            target: { kind: "trend" },
            source: "activities: mean days since last touch",
            jobRefs: ["customer-success.surface-at-risk-accounts"],
            description: "How warm the book is.",
        },
        {
            id: "customer-success.sla-adherence",
            label: "Support SLA",
            unit: "percent",
            direction: "increase",
            target: { kind: "fixed", value: 95 },
            source: "support items within SLA / total",
            jobRefs: ["customer-success.triage-support-queue"],
            description: "Requests handled inside their SLA.",
        },
    ],
    jobs: [
        {
            id: "customer-success.triage-support-queue",
            statement: "When support requests arrive, I want them triaged and routed so nothing waits past its SLA.",
            cadence: "daily",
            appliesWhen: "has-customers",
            trigger: {
                source: "tasks",
                condition: "open support items aging past SLA",
            },
            definitionOfDone: "No support item past SLA without an owner.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: { state: "partial" },
            workflow: { state: "partial", ref: "ticket-triage-orchestrator loop" },
            dashboard: { state: "exists", ref: "support volume KPI" },
            playAction: { state: "gap" },
            review: { priority: 3.4, confidence: 3.2, impact: 3.4, spread: 1.2 },
        },
        {
            id: "customer-success.surface-at-risk-accounts",
            statement: "When an account goes quiet or shows churn signals, I want it surfaced so I can intervene before it churns.",
            cadence: "weekly",
            appliesWhen: "has-customers",
            trigger: {
                source: "crm_opportunities",
                condition: "customer with no activity 60+ days or negative health signal",
            },
            definitionOfDone: "Every at-risk account has an intervention or a note.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: { state: "partial", ref: "CRM-derived health bands" },
            workflow: { state: "partial", ref: "churn-save / high-touch-save loops" },
            dashboard: { state: "exists", ref: "at-risk + accounts-by-health KPIs" },
            playAction: { state: "gap" },
            review: { priority: 3.6, confidence: 3.0, impact: 4.0, spread: 1.5 },
        },
        {
            id: "customer-success.drive-renewals",
            statement: "When renewals approach, I want them worked so revenue doesn't leak through lapsed contracts.",
            cadence: "weekly",
            appliesWhen: "has-revenue",
            trigger: {
                source: "crm_opportunities",
                condition: "renewal due within the window",
            },
            definitionOfDone: "Every upcoming renewal is in motion before its date.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: { state: "partial" },
            workflow: { state: "partial", ref: "renewal-motion loop" },
            dashboard: { state: "exists", ref: "renewals-due KPI" },
            playAction: { state: "gap" },
            review: { priority: 3.4, confidence: 3.0, impact: 3.8, spread: 1.3 },
        },
        {
            id: "customer-success.maintain-help-content",
            statement: "When recurring questions lack a help article, I want content created/updated so support deflects itself.",
            cadence: "monthly",
            appliesWhen: "has-customers",
            trigger: {
                source: "section_audit_findings",
                condition: "support audit finds help-content gaps",
            },
            definitionOfDone: "Top recurring questions have current help content.",
            owner: "hybrid",
            maturity: "manual",
            tooling: { state: "partial" },
            workflow: { state: "partial", ref: "kb-self-healing loop" },
            dashboard: { state: "gap" },
            playAction: { state: "gap" },
            review: { priority: 2.6, confidence: 2.8, impact: 3.0, spread: 1.0 },
        },
    ],
};
const FINANCE = {
    slug: "finance",
    name: "Finance",
    kind: "section",
    scope: "project",
    mission: "Keep the company solvent and the money clean — books reconciled, runway known, receivables collected, and the month closed on time.",
    managerQuestion: "Are we solvent, and is the money clean?",
    northStar: {
        id: "finance.runway",
        label: "Runway",
        unit: "months",
        direction: "hold-above",
        target: { kind: "brief", field: "finance.runwayFloorMonths", fallback: 6 },
        source: "money_accounts cash ÷ trailing monthly net burn",
        jobRefs: ["finance.watch-runway"],
        description: "Months of cash at the current burn.",
    },
    secondaryKpis: [
        {
            id: "finance.revenue",
            label: "Revenue",
            unit: "currency",
            direction: "increase",
            target: { kind: "trend" },
            source: "transactions on revenue accounts (range)",
            jobRefs: ["finance.reconcile-the-books"],
            description: "Is money coming in?",
        },
        {
            id: "finance.net-burn",
            label: "Net",
            unit: "currency",
            direction: "increase",
            target: { kind: "trend" },
            source: "transactions: revenue − expense (range)",
            jobRefs: ["finance.watch-runway"],
            description: "Profitable this window?",
        },
        {
            id: "finance.unmatched-txns",
            label: "Unmatched txns",
            unit: "count",
            direction: "decrease",
            target: { kind: "fixed", value: 0 },
            source: "transactions.counterparty_kind IS NULL",
            jobRefs: ["finance.reconcile-the-books"],
            description: "Is the ledger clean?",
        },
        {
            id: "finance.last-sync",
            label: "Last sync",
            unit: "days",
            direction: "decrease",
            target: { kind: "fixed", value: 1 },
            source: "integration_connections.last_synced_at (finance)",
            jobRefs: ["finance.reconcile-the-books"],
            description: "Is the data current?",
        },
    ],
    jobs: [
        {
            id: "finance.reconcile-the-books",
            statement: "When transactions are unmatched or uncategorized, I want them reconciled so the books reflect reality.",
            cadence: "weekly",
            appliesWhen: "has-revenue",
            trigger: {
                source: "transactions",
                condition: "unmatched or uncategorized transactions exist",
            },
            definitionOfDone: "No unmatched transactions older than the window.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: { state: "exists", ref: "finance dashboard + integrations" },
            workflow: { state: "partial", ref: "expense-to-book loop" },
            dashboard: { state: "exists", ref: "unmatched-txns KPI" },
            playAction: { state: "gap" },
            review: { priority: 3.4, confidence: 3.4, impact: 3.6, spread: 1.2 },
        },
        {
            id: "finance.watch-runway",
            statement: "When runway shortens or burn spikes, I want it surfaced early so I can act before it's urgent.",
            cadence: "weekly",
            appliesWhen: "has-revenue",
            trigger: {
                source: "money_accounts",
                condition: "runway below threshold or burn above trend",
            },
            definitionOfDone: "Runway is current and within a known, acceptable band.",
            owner: "hybrid",
            maturity: "tool-assisted",
            tooling: { state: "exists", ref: "finance dashboard (runway)" },
            workflow: { state: "gap" },
            dashboard: { state: "exists", ref: "runway KPI" },
            playAction: { state: "gap" },
            review: { priority: 3.8, confidence: 3.4, impact: 4.2, spread: 1.4 },
        },
        {
            id: "finance.collect-receivables",
            statement: "When invoices go overdue, I want a dunning motion so cash isn't left on the table.",
            cadence: "weekly",
            appliesWhen: "has-revenue",
            trigger: {
                source: "transactions",
                condition: "receivable past due",
            },
            definitionOfDone: "No receivable past due without a dunning step.",
            owner: "hybrid",
            maturity: "manual",
            tooling: { state: "partial" },
            workflow: { state: "partial", ref: "dunning-ladder loop" },
            dashboard: { state: "partial" },
            playAction: { state: "gap" },
            review: { priority: 3.0, confidence: 3.0, impact: 3.4, spread: 1.1 },
        },
        {
            id: "finance.close-the-month",
            statement: "When the month ends, I want the close completed on a checklist so reporting is timely and trustworthy.",
            cadence: "monthly",
            appliesWhen: "has-revenue",
            trigger: {
                source: "transactions",
                condition: "month ended and close checklist incomplete",
            },
            definitionOfDone: "Month-end close completed within target days.",
            owner: "human",
            maturity: "manual",
            tooling: { state: "gap" },
            workflow: { state: "partial", ref: "month-end-close loop" },
            dashboard: { state: "gap" },
            playAction: { state: "gap" },
            review: { priority: 2.8, confidence: 2.8, impact: 3.2, spread: 1.0 },
        },
    ],
};
const LEGAL = {
    slug: "legal",
    name: "Legal",
    kind: "section",
    scope: "project",
    mission: "Keep the company compliant and its paperwork current — policies live, obligations tracked, contracts managed, and IP protected.",
    managerQuestion: "Are we compliant, and is the paperwork current?",
    northStar: {
        id: "legal.compliance-coverage",
        label: "Compliance coverage",
        unit: "percent",
        direction: "increase",
        target: { kind: "fixed", value: 100 },
        source: "section_audit_findings + legal brief + documents — obligations/policies current",
        jobRefs: [
            "legal.keep-policies-current",
            "legal.track-compliance-obligations",
        ],
        description: "Applicable obligations and required policies present, owned, and current.",
    },
    secondaryKpis: [
        {
            id: "legal.review-cadence",
            label: "Review cadence",
            unit: "days",
            direction: "decrease",
            target: { kind: "brief", field: "legal.reviewCadenceDays", fallback: 90 },
            source: "legal brief: days since last compliance review",
            jobRefs: ["legal.keep-policies-current"],
            description: "Adherence to the review cadence.",
        },
        {
            id: "legal.contracts-expiring",
            label: "Contracts expiring",
            unit: "count",
            direction: "hold-below",
            target: { kind: "trend" },
            source: "documents/contracts with expiry in window (enrichment-gated)",
            jobRefs: ["legal.manage-contracts"],
            description: "Agreements approaching renewal/expiry.",
        },
        {
            id: "legal.tracked-docs",
            label: "Tracked documents",
            unit: "count",
            direction: "increase",
            target: { kind: "trend" },
            source: "documents tagged legal / attachments",
            jobRefs: ["legal.track-compliance-obligations"],
            description: "Policies and contracts logged.",
        },
        {
            id: "legal.brief-completeness",
            label: "Brief completeness",
            unit: "percent",
            direction: "increase",
            target: { kind: "fixed", value: 100 },
            source: "excellent_section_briefs (legal) completeness",
            jobRefs: ["legal.keep-policies-current"],
            description: "Is the entity's legal posture documented?",
        },
    ],
    jobs: [
        {
            id: "legal.keep-policies-current",
            statement: "When a required policy (privacy, terms) is missing or stale, I want it drafted/updated so the company isn't exposed.",
            cadence: "monthly",
            appliesWhen: "always",
            trigger: {
                source: "section_audit_findings",
                condition: "legal audit finds a missing or stale policy",
            },
            definitionOfDone: "Privacy policy and terms exist and are current.",
            owner: "hybrid",
            maturity: "manual",
            tooling: { state: "partial", ref: "legal brief + audit" },
            workflow: { state: "gap" },
            dashboard: { state: "exists", ref: "compliance-review-status KPI" },
            playAction: { state: "gap" },
            review: { priority: 3.0, confidence: 2.6, impact: 3.4, spread: 1.3 },
        },
        {
            id: "legal.track-compliance-obligations",
            statement: "When a compliance regime applies (GDPR/CCPA/SOC 2), I want its obligations tracked so deadlines aren't missed.",
            cadence: "monthly",
            appliesWhen: "has-customers",
            trigger: {
                source: "section_audit_findings",
                condition: "legal audit finds an untracked applicable obligation",
            },
            definitionOfDone: "Every applicable obligation has an owner and a status.",
            owner: "hybrid",
            maturity: "manual",
            tooling: { state: "partial" },
            workflow: { state: "gap" },
            dashboard: { state: "partial" },
            playAction: { state: "gap" },
            review: { priority: 2.6, confidence: 2.4, impact: 3.2, spread: 1.2 },
        },
        {
            id: "legal.manage-contracts",
            statement: "When a contract needs review or is approaching renewal/expiry, I want it surfaced so nothing auto-renews or lapses unseen.",
            cadence: "monthly",
            appliesWhen: "team",
            trigger: {
                source: "section_audit_findings",
                condition: "legal audit finds a contract needing review/renewal",
            },
            definitionOfDone: "Key contracts have owners and known renewal/expiry dates.",
            owner: "human",
            maturity: "manual",
            tooling: { state: "gap", note: "no contracts register yet" },
            workflow: { state: "gap" },
            dashboard: { state: "gap" },
            playAction: { state: "gap" },
            review: { priority: 2.4, confidence: 2.2, impact: 3.0, spread: 1.1 },
        },
    ],
};
/**
 * The 10 departments, in display order: the 9 sections (rail order) then the
 * synthetic Agents department.
 */
export const DEPARTMENTS = [
    OPERATIONS,
    DESIGN,
    HIRING,
    DEVELOPMENT,
    MARKETING,
    SALES,
    CUSTOMER_SUCCESS,
    FINANCE,
    LEGAL,
    AGENTS,
];
const DEPARTMENT_BY_SLUG = Object.fromEntries(DEPARTMENTS.map((d) => [d.slug, d]));
const JOB_BY_ID = Object.fromEntries(DEPARTMENTS.flatMap((d) => d.jobs.map((j) => [j.id, j])));
export function getDepartment(slug) {
    return DEPARTMENT_BY_SLUG[slug] ?? null;
}
export function getJob(id) {
    return JOB_BY_ID[id] ?? null;
}
export function jobsForDepartment(slug) {
    return DEPARTMENT_BY_SLUG[slug]?.jobs ?? [];
}
export function departmentForJob(id) {
    const dept = id.split(".")[0] ?? "";
    return DEPARTMENT_BY_SLUG[dept] ?? null;
}
export const ALL_JOBS = DEPARTMENTS.flatMap((d) => d.jobs);
export const DEPARTMENT_SLUGS = DEPARTMENTS.map((d) => d.slug);
/**
 * The option list for a goal's `domain` field (the department axis, v144) —
 * DERIVED from the department registry so it can never carry a value the
 * registry doesn't. This is the ONE place the vocabulary is read from: both
 * `isValidGoalDomain` (the write-seam validator) and the goal peek's `domain`
 * select descriptor source their options from here, never a second copy or an
 * inline string union.
 */
export function goalDomainOptions() {
    return DEPARTMENTS.map((d) => ({ value: d.slug, label: d.name }));
}
/** Is `domain` a recognized department slug? `kind` (v133) is a frozen CHECK
 *  column — this is the code-side validation `domain` relies on INSTEAD of a
 *  CHECK, so the vocabulary can grow without a table rebuild. */
export function isValidGoalDomain(domain) {
    return DEPARTMENT_SLUGS.includes(domain);
}
